import React from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { BRAND, MEDIA } from "../lib/constants";

export default function About() {
  return (
    <>
      <SEO
        title="About District Wellness Center | Licensed DC Dispensary"
        description="DWC is a locally operated, licensed medical cannabis dispensary with three Washington DC locations. Our story, our standard, and how we serve DC patients."
        path="/about"
        breadcrumbs={[{ name: "About", path: "/about" }]}
      />

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Our Story</div>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              Wellness, <em className="text-brand">curated</em><br/>for the District.
            </h1>
          </div>
          <div className="lg:col-span-4 text-ink-muted text-lg leading-relaxed">
            District Wellness Center exists because DC patients deserved better. Better sourcing, better service,
            and a better room to step into.
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-24">
        <div className="max-w-5xl mx-auto px-6 prose prose-lg font-sans text-ink-muted">
          <p className="text-xl leading-relaxed">
            DWC started with a simple idea: treat medical cannabis like the wellness category it is —
            meticulously sourced, honestly merchandised, and served by people who actually know what they're
            talking about. No black-light stoner tropes. No mystery gummies. Just thoughtfully curated products
            from DC's best cultivators and processors.
          </p>
          <p>
            Our flagship on Georgia Ave NW carries a carefully curated menu. Every product goes through our intake bar —
            lab results reviewed, packaging checked, effects profiled. We reject far more than we accept.
          </p>
          <p>
            We believe the best dispensary is the one that makes you feel like a patient, not a customer.
            Ask us anything. We'll give you a straight answer.
          </p>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <img src={MEDIA.storefrontDoor} alt="Customer entering District Wellness Center at 4915 Georgia Ave NW, Washington DC" className="w-full h-[500px] object-cover border border-black/10" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">The Standard</div>
            <h2 className="font-serif text-5xl leading-[0.95]">Curated. Compliant. Cared-for.</h2>
            <ul className="mt-8 space-y-4 text-ink-muted">
              <li className="flex gap-3"><span className="text-brand">✦</span> Every batch reviewed against DC OCA compliance</li>
              <li className="flex gap-3"><span className="text-brand">✦</span> Lab results available on request, always</li>
              <li className="flex gap-3"><span className="text-brand">✦</span> Staff trained in cannabis wellness & dosing</li>
              <li className="flex gap-3"><span className="text-brand">✦</span> Proceeds re-invested into the DC community</li>
            </ul>
            <Link to="/locations" className="mt-8 inline-flex items-center gap-2 bg-ink text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em]" data-testid="about-locations-btn">
              Visit Any Location →
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-ink text-white py-16" data-testid="license-bar">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-2 flex justify-center md:justify-start">
            <ShieldCheck size={56} className="text-brand" />
          </div>
          <div className="md:col-span-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">Licensed &amp; Compliant</div>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-4">
              Licensed by the DC Alcoholic Beverage &amp; Cannabis Administration.
            </h2>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3 text-base text-white/80">
              <div><span className="text-white/50 text-[11px] uppercase tracking-[0.25em] block mb-1">License Number</span>{BRAND.licenseNumber}</div>
              <div><span className="text-white/50 text-[11px] uppercase tracking-[0.25em] block mb-1">Class</span>Retailer (Medical Cannabis)</div>
              <div><span className="text-white/50 text-[11px] uppercase tracking-[0.25em] block mb-1">Legal Entity</span>{BRAND.legalName}</div>
              <div><span className="text-white/50 text-[11px] uppercase tracking-[0.25em] block mb-1">Trading As</span>{BRAND.dba}</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
